import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { X } from "lucide-react";

const BRACKET_LABELS = {
  main: "Bracket", winners: "Winners Bracket", losers: "Losers Bracket",
  final: "Finals", playoff: "Playoffs", rr: "Round Robin",
};

export default function BracketView({ tournamentId, matches, entries, canManage, onChange, seeds = {} }) {
  const [scoring, setScoring] = useState(null);
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);

  const nameOf = (id) => {
    if (!id) return "TBD";
    if (id === "BYE") return "— Bye —";
    return entries.find((e) => e.id === id)?.display_name || "Unknown";
  };

  const heldIds = new Set((entries || []).filter((e) => e.on_hold).map((e) => e.id));

  const brackets = {};
  matches.forEach((m) => {
    brackets[m.bracket] = brackets[m.bracket] || {};
    (brackets[m.bracket][m.round] = brackets[m.bracket][m.round] || []).push(m);
  });
  const order = ["winners", "main", "rr", ...Object.keys(brackets).filter((b) => b.startsWith("pool-")).sort(), "losers", "playoff", "final"];
  const present = order.filter((b) => brackets[b]);

  const submitScore = async () => {
    setBusy(true);
    try {
      const bo = scoring.best_of || 1;
      let body;
      if (bo > 1) {
        const filled = games.filter((g) => g.a !== "" && g.b !== "").map((g) => [Number(g.a), Number(g.b)]);
        const wa = filled.filter(([a, b]) => a > b).length;
        const wb = filled.filter(([a, b]) => b > a).length;
        const need = Math.floor(bo / 2) + 1;
        if (wa < need && wb < need) { toast.error(`Best of ${bo} — a side must win ${need} games`); setBusy(false); return; }
        body = { score_a: wa, score_b: wb, games: filled };
      } else {
        body = { score_a: Number(scoreA), score_b: Number(scoreB) };
      }
      await api.post(`/tournaments/${tournamentId}/matches/${scoring.key}/result`, body);
      toast.success("Score recorded");
      setScoring(null); setScoreA(""); setScoreB(""); setGames([]);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openScoring = (m) => {
    setScoring(m);
    setScoreA(m.score_a ?? ""); setScoreB(m.score_b ?? "");
    const bo = m.best_of || 1;
    if (bo > 1) {
      setGames(Array.from({ length: bo }, (_, i) => ({
        a: m.games?.[i]?.a ?? "", b: m.games?.[i]?.b ?? "" })));
    } else setGames([]);
  };

  return (
    <div className="space-y-8" data-testid="bracket-view">
      {present.map((b) => (
        <div key={b}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-3">{BRACKET_LABELS[b] || `Pool ${b.replace("pool-", "")}`}</div>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {Object.keys(brackets[b]).sort((x, y) => Number(x) - Number(y)).map((rnd) => (
              <div key={rnd} className="flex flex-col justify-around gap-3 min-w-[220px]">
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 text-center">Round {rnd}</div>
                {brackets[b][rnd].map((m) => {
                  const playable = canManage && m.side_a && m.side_b && m.side_a !== "BYE" && m.side_b !== "BYE";
                  return (
                    <div key={m.key} data-testid={`match-${m.key}`}
                      onClick={() => { if (playable) openScoring(m); }}
                      className={`border bg-pb-elevated text-xs ${playable ? "border-pb-cyan/50 cursor-pointer hover:border-pb-cyan" : m.status === "complete" ? "border-pb-border" : "border-pb-border/50"}`}>
                      {[["side_a", "score_a", "a"], ["side_b", "score_b", "b"]].map(([side, sc, ab]) => {
                        const serving = m.court && m.status !== "complete" && m.first_serve === ab;
                        return (
                        <div key={side} data-testid={`match-${m.key}-${side}`} data-serving={serving ? "true" : undefined}
                          className={`flex items-center justify-between px-3 py-2 ${serving ? "serve-box" : ""} ${side === "side_a" ? "border-b border-pb-border/50" : ""} ${m.winner_id && m.winner_id === m[side] ? "text-pb-red font-bold" : m[side] === "BYE" ? "text-zinc-600 italic" : "text-zinc-200"}`}>
                          <span className="truncate max-w-[150px]">
                            {["playoff", "final", "main", "winners", "losers"].includes(b) && seeds[m[side]] ? (
                              <span className="text-pb-red font-mono font-black mr-1" data-testid={`seed-${m.key}-${side}`}>#{seeds[m[side]]}</span>
                            ) : null}
                            {nameOf(m[side])}
                          </span>
                          <span className="font-mono flex items-center gap-2">{serving && <span className="text-[8px] font-black uppercase tracking-widest text-red-400">Serve</span>}{m[sc] ?? ""}</span>
                        </div>
                        );
                      })}
                      {(m.best_of || 1) > 1 && (
                        <div className="text-[8px] uppercase tracking-widest text-amber-400 bg-amber-500/10 px-3 py-1 border-t border-amber-500/30" data-testid={`match-bestof-${m.key}`}>
                          Best of {m.best_of}{m.games?.length ? ` · ${m.games.map((g) => `${g.a}-${g.b}`).join(" · ")}` : ""}
                        </div>
                      )}
                      {(m.division || m.age_bracket) && (
                        <div className="text-[8px] uppercase tracking-widest text-pb-cyan bg-pb-cyan/10 px-3 py-0.5 border-b border-pb-cyan/20" data-testid={`match-division-${m.key}`}>
                          {[m.division, m.age_bracket].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      {m.status !== "complete" && (heldIds.has(m.side_a) || heldIds.has(m.side_b)) && (
                        <div className="text-[8px] uppercase tracking-widest text-amber-400 bg-amber-500/10 px-3 py-1 border-t border-amber-500/30" data-testid={`match-hold-${m.key}`}>⏸ On Hold — team running late</div>
                      )}
                      {m.court && m.status !== "complete" && (
                        <div className="text-[8px] uppercase tracking-widest text-green-400 bg-green-500/10 px-3 py-1 border-t border-green-500/30" data-testid={`match-court-${m.key}`}>Court {m.court}</div>
                      )}
                      {playable && <div className="text-[8px] uppercase tracking-widest text-pb-cyan px-3 py-1 border-t border-pb-border/50">{m.status === "complete" ? "Tap to edit score" : "Tap to score"}</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ))}

      {scoring && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setScoring(null)}>
          <div className="bg-pb-surface border border-pb-red w-full max-w-sm p-5 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="score-modal">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-black uppercase">Record Score</span>
              <button onClick={() => setScoring(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {(scoring.best_of || 1) > 1 ? (
              <>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Best of {scoring.best_of} — enter each game (leave unplayed games blank)</div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 items-center mb-3">
                  <span />
                  <span className="text-[9px] text-zinc-500 uppercase tracking-widest text-center w-16 truncate">{nameOf(scoring.side_a)}</span>
                  <span className="text-[9px] text-zinc-500 uppercase tracking-widest text-center w-16 truncate">{nameOf(scoring.side_b)}</span>
                  {games.map((g, i) => (
                    <div key={i} className="contents">
                      <span className="text-xs text-zinc-400">Game {i + 1}</span>
                      <input data-testid={`game-${i}-a`} type="number" min={0} value={g.a}
                        onChange={(e) => setGames((p) => p.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))}
                        className="w-16 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-center focus:outline-none focus:border-pb-red" />
                      <input data-testid={`game-${i}-b`} type="number" min={0} value={g.b}
                        onChange={(e) => setGames((p) => p.map((x, j) => (j === i ? { ...x, b: e.target.value } : x)))}
                        className="w-16 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-center focus:outline-none focus:border-pb-red" />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              [["a", scoring.side_a, scoreA, setScoreA], ["b", scoring.side_b, scoreB, setScoreB]].map(([k, side, val, set]) => (
                <div key={k} className="flex items-center justify-between gap-3 mb-3">
                  <span className="text-sm truncate">{nameOf(side)}</span>
                  <input data-testid={`score-${k}`} type="number" min={0} value={val} onChange={(e) => set(e.target.value)}
                    className="w-20 bg-pb-elevated border border-pb-border px-2 py-2 text-white font-mono text-center focus:outline-none focus:border-pb-red" />
                </div>
              ))
            )}
            <button data-testid="score-submit" onClick={submitScore}
              disabled={busy || ((scoring.best_of || 1) > 1 ? !games.some((g) => g.a !== "" && g.b !== "") : scoreA === "" || scoreB === "")}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {busy ? "Saving…" : "Save Result"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
